import { AppSidebar } from '@/components/app-sidebar';
import { DemoModePanel } from '@/components/DemoModePanel';
import { ErrorState } from '@/components/ErrorState';
import { ExhibitionClaimBanner } from '@/components/ExhibitionClaimBanner';
import { Loading } from '@/components/Loading';
import { getHttpErrorMessage } from '@/lib/httpErrorMessage';
import { SiteHeader } from '@/components/site-header';
import {
  SidebarInset,
  SidebarProvider
} from '@/components/ui/sidebar';
import { useAuth } from '@/lib/auth';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface AppLayoutProps {
  children: React.ReactNode;
  breadcrumbs?: {
    href: string;
    children: string;
  }[];
  newButton?: 'invoice' | 'order' | 'expense' | 'participant';
  hideHeader?: boolean;
  contentClassName?: string;
}

export const AppLayout = ({
  children,
  breadcrumbs,
  newButton,
  hideHeader = false,
  contentClassName,
}: AppLayoutProps) => {
  return (
    <Auth>
      <div className="[--header-height:3rem] min-h-svh min-w-0 bg-sidebar">
        <SidebarProvider>
          <AppSidebar />
          <SidebarInset
            className={cn(
              'min-h-svh min-w-0 bg-background md:peer-data-[variant=inset]:rounded-md',
              hideHeader && 'overflow-hidden',
            )}
          >
            {!hideHeader && (
              <SiteHeader breadcrumbs={breadcrumbs || []} newButton={newButton} />
            )}
            <ExhibitionClaimBanner />
            <div
              data-slot="app-main-content"
              className={cn('flex min-w-0 flex-1 flex-col', contentClassName ?? 'gap-4 p-4')}
            >
              {children}
            </div>
          </SidebarInset>
        </SidebarProvider>
      </div>
    </Auth>
  );
};

function Auth({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { user, isLoading, error, refetchUser } = useAuth({
    middleware: 'auth',
    redirectTo: '/auth/login',
    tmp: 'AppLayout',
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState>
        <h1 className="text-lg font-semibold">{t('generic.error')}</h1>
        <p className="text-sm">{getHttpErrorMessage(error, t('generic.loadError'))}</p>
        <button
          type="button"
          className="mt-2 text-sm underline"
          onClick={() => void refetchUser()}
        >
          {t('generic.retry')}
        </button>
      </ErrorState>
    );
  }

  if (!user) {
    return <Loading />;
  }

  return (
    <>
      {children}
      <DemoModePanel user={user} />
    </>
  );
}

AppLayout.displayName = 'AppLayout';
